import { useState, useCallback, useRef, useEffect } from 'react';
import type { Room } from 'livekit-client';
import {
  isAgentParticipant,
  ANAKA_SESSION_END_ATTR,
  parseVoiceEndReason,
  type VoiceEndReason,
} from '@shared/livekit-constants';
import {
  CLIENT_TURN_STALL_MS,
  CLIENT_THINKING_ABSOLUTE_CEILING_MS,
  VOICE_MAX_SESSION_MS,
} from '@shared/conversation-timeouts';
import {
  VOICE_TRANSCRIPT_EVENT,
  parseVoiceTranscriptMessage,
  type VoiceTranscriptData,
} from '@shared/voice-data-channel';
import {
  classifyMicError,
  greetingTimeoutMessage,
  micErrorHint,
  readMicPermission,
  type MicErrorKind,
} from './micError';
import { publishIdleCheckIn } from './idleCheckIn';
import { captureVoiceEvent, currentVisibilityState } from '@/lib/voice-telemetry';

export type VoiceState =
  | 'idle'
  | 'authorizing'
  | 'connecting'
  | 'preparing'
  | 'listening'
  | 'thinking'
  | 'speaking'
  | 'reconnecting'
  | 'disconnected'
  // ANA-575 — the agent INTENTIONALLY ended the session (goodbye / scope-lock /
  // cap / guardrail). Terminal + distinct from `disconnected` (an unexpected
  // transport drop → "Ready when you are" + Restart) and `error`. Painted on the
  // real disconnect (after the goodbye plays), never overwritten by a later
  // `disconnected`. `error`-reason ends do NOT use this — they keep the retryable
  // error surface.
  | 'ended'
  | 'error';

/**
 * Participant attribute the LiveKit agent publishes for its lifecycle state
 * (`initializing` → `listening` → `thinking` → `speaking`). This is the same
 * signal `@livekit/components-react`'s `useVoiceAssistant` reads; we watch it to
 * know when Anaka actually starts speaking the greeting.
 */
const AGENT_STATE_ATTR = 'lk.agent.state';

/**
 * Safety net for the `preparing` (loading) phase: if the agent never speaks
 * (silent agent, a failed `/turn`, a degraded backend), don't hang on the
 * spinner forever — fall back to the active `listening` state so the user can
 * still talk.
 */
const PREPARING_FALLBACK_MS = 20_000;

/**
 * IP-260703 — how long the session may sit in `thinking` (Anaka processing a
 * turn) with no agent-state transition and no `status_label` heartbeat before we
 * treat the turn as stalled (dropped agent) and surface a retryable error.
 * Generous, because a legitimate tool-loop turn can run for a while; the
 * heartbeat re-arm keeps a long-but-alive turn from tripping it.
 */
const THINKING_STALL_MS = CLIENT_TURN_STALL_MS;

/**
 * ANA-727 (A1 / R5) — absolute ceiling on a single continuous `thinking` EPISODE.
 * The 45s stall above is re-armed by every status-label heartbeat, so a CONCURRENT
 * turn's heartbeats can keep a UI wedged on the OLD turn "Thinking" forever. This
 * ceiling is anchored at the first `listening→thinking` of a turn and a heartbeat
 * CANNOT defer it. Shared const so the server owns the wall-clock-vs-ceiling
 * invariant (see conversation-timeouts.ts + getTurnWallclockBudgetMs).
 */
const THINKING_ABSOLUTE_CEILING_MS = CLIENT_THINKING_ABSOLUTE_CEILING_MS;

/**
 * ANA-727 (A2 / R3) — the `listening` liveness watchdog. After the user speaks a
 * substantial utterance (≥ MIN_SPEECH) that ends with no agent-state transition
 * (STT never committed a turn — the incident's zero-EndOfTurn signature), arm a
 * timer; if `listening` persists this long, surface a retryable error. The
 * MIN_SPEECH guard filters coughs/blips that Flux legitimately declines to commit.
 */
const LISTENING_STALL_MS = 20_000;
const LISTENING_WATCHDOG_MIN_SPEECH_MS = 2_000;

/**
 * ANA-727 (A2 / R6) — the listening watchdog's TEARDOWN is behind this flag
 * (default OFF → enable staging first), so it can't ride the next routine prod
 * release before its false-positive rate is measured. Build-time Vite env, same
 * pattern as VOICE_RESUME_GREETING_CYCLE_ENABLED below.
 */
const VOICE_LISTENING_WATCHDOG_ENABLED =
  (import.meta.env.VITE_VOICE_LISTENING_WATCHDOG ?? 'false') === 'true';

/**
 * ANA-728 (B1 / R6/R7) — auto mic-lock during `thinking`. Default OFF (staging
 * build env sets it true) so a privacy-sensitive, product-visible behaviour never
 * rides the next prod release un-vetted. Build-time Vite env.
 */
const VOICE_MIC_LOCK_ENABLED =
  (import.meta.env.VITE_VOICE_MIC_LOCK ?? 'false') === 'true';

/**
 * ANA-728 (B1 / R7) — grace before the mic-lock engages after entering `thinking`.
 * A reflective speaker who pauses mid-thought and keeps talking (a clipped-thought
 * continuation) should still be HEARD (it lands as a supersede); only the later
 * impatience-probe window ("hello?") is the harmful one the lock closes.
 */
const MIC_LOCK_GRACE_MS = 2_500;

/**
 * Hard cap on a single continuous voice connection — a cost backstop ON TOP of
 * the idle auto-pause. When a room has been connected this long we pause it
 * (releases the mic + the billable LiveKit room; resumable). Covers the cases
 * the idle pause can't: idle detection defeated by continuous background noise,
 * or a non-listening stuck state. 60 minutes is generous enough not to cut off a
 * real conversation while still bounding worst-case cost.
 *
 * ANA-409 adds a pre-cap warning (`sessionCapWarning`) + `extendSession()` so
 * the surface can wind the call down gracefully as the cap approaches, rather
 * than yanking the room with zero notice. The hard pause itself, and what
 * happens if the user never responds to the warning, are UNCHANGED.
 */
const MAX_SESSION_MS = VOICE_MAX_SESSION_MS;

/**
 * ANA-409 — how long before the hard cost-cap the `sessionCapWarning` fires,
 * giving the surface time to offer "continue or lock the scope" before the
 * room is yanked. Defaults to 2 minutes; set small in tests.
 */
const SESSION_CAP_WARNING_LEAD_MS = 2 * 60 * 1000;

/**
 * Grace period after the browser tab is hidden before we pause the call. A quick
 * tab-flick (back before the grace elapses) won't tear the call down, but
 * actually leaving for the window releases the mic + room. We pause on the
 * `visibilitychange` event rather than relying on the idle auto-pause because
 * browsers THROTTLE background-tab timers, so the idle timer can fire late (or
 * effectively never within a useful window) on a hidden tab.
 */
const HIDDEN_GRACE_MS = 15_000;

/**
 * Bug B (ANA-614) kill-switch — MUST mirror the identically-named const in
 * `useConversation.ts`. Both modules read the SAME build-time Vite env so the
 * whole feature (the `connectVoice` cold-start/resume path AND the pause→resume
 * `resume()` path here) toggles together:
 * ON (default): a pause→resume degrades a slow/absent recap 'preparing' →
 *   'listening' (the recap is a full LLM turn that can be slow — a resume must
 *   not surface the cold-start error).
 * OFF (`VITE_VOICE_RESUME_GREETING_CYCLE=false`): restores the EXACT prior
 *   behaviour on this path too — `resume()` arms the greeting timeout as a HARD
 *   error (spec-025 FR-006), so a genuinely wedged pause→resume (e.g. the /turn
 *   401 incident, or a mic held by another app) still surfaces the honest,
 *   retryable error + mic-blocked detection rather than silently degrading.
 */
const VOICE_RESUME_GREETING_CYCLE_ENABLED =
  (import.meta.env.VITE_VOICE_RESUME_GREETING_CYCLE ?? 'true') !== 'false';

export interface VoiceConversationOptions {
  projectId: string;
  mode: string;
  conversationId: string;
  activityId?: string;
  participantId?: string;
  /**
   * 015 (N4) — after this many ms of no user response while `listening`, surface
   * a "still there?" prompt (does NOT pause yet). If still no activity after
   * `idleStillThereGraceMs`, auto-Pause (stops the billable room) like a manual
   * Pause. Defaults to 60_000 (1 min); set small in tests.
   */
  idleListeningTimeoutMs?: number;
  /**
   * Grace window after the "still there?" prompt before we auto-Pause an
   * unattended `listening` session. Defaults to 30_000 (30s); set small in tests.
   */
  idleStillThereGraceMs?: number;
  /**
   * Spec 025 (US1 / FR-006) — if Anaka's greeting never arrives within this
   * window after a fresh voice-first connect, surface a retryable error instead
   * of silently dropping to `listening` (the /turn 401 incident left the user in
   * a dead-but-active-looking session). Defaults to PREPARING_FALLBACK_MS; set
   * small in tests.
   */
  greetingTimeoutMs?: number;
  /**
   * IP-260703 (client recovery) — if the session sits in `thinking` (Anaka is
   * processing a turn) longer than this with no agent-state transition and no
   * heartbeat (a `status_label` update), surface a retryable error instead of an
   * infinite "Thinking" spinner. This is the backstop for a dropped agent —
   * a mid-call redeploy, crash, or scale-in that tears down the in-flight session
   * (n8n/Cloud returned fine; the browser just never heard back). Each
   * `status_label` the agent publishes re-arms the window, so a long-but-alive
   * turn won't false-error. Defaults to THINKING_STALL_MS; set small in tests.
   */
  thinkingStallTimeoutMs?: number;
  /**
   * ANA-727 (A1) — absolute ceiling on a single `thinking` episode (heartbeats
   * cannot defer it). Defaults to THINKING_ABSOLUTE_CEILING_MS; set small in tests.
   */
  thinkingCeilingMs?: number;
  /**
   * ANA-727 (A2) — after the user speaks a ≥`listeningWatchdogMinSpeechMs` utterance
   * that ends with no agent-state transition, how long `listening` may persist
   * before surfacing a retryable error (a dead STT leg). Defaults to LISTENING_STALL_MS;
   * set small in tests.
   */
  listeningStallMs?: number;
  /** ANA-727 (A2) — minimum speech-episode duration to arm the listening watchdog
   * (filters coughs/blips). Defaults to LISTENING_WATCHDOG_MIN_SPEECH_MS. */
  listeningWatchdogMinSpeechMs?: number;
  /** ANA-727 (A2 / R6) — whether the listening watchdog TEARS DOWN on fire
   * (vs telemetry-only). Defaults to the VITE_VOICE_LISTENING_WATCHDOG build flag;
   * an explicit value overrides it (tests). */
  listeningWatchdogEnforced?: boolean;
  /** ANA-728 (B1 / R6) — whether the mic auto-lock is active. Defaults to the
   * VITE_VOICE_MIC_LOCK build flag; an explicit value overrides it (tests). */
  micLockEnabled?: boolean;
  /** ANA-728 (B1) — grace before the mic-lock engages after entering thinking.
   * Defaults to MIC_LOCK_GRACE_MS; set small in tests. */
  micLockGraceMs?: number;
  /**
   * Cost backstop — hard cap on a single continuous connection before we
   * auto-pause regardless of activity. Defaults to MAX_SESSION_MS (60 min); set
   * small in tests.
   */
  maxSessionMs?: number;
  /**
   * ANA-409 — how long before `maxSessionMs` the surface should warn the user
   * (`sessionCapWarning`) that the call is about to be wound down. Defaults to
   * SESSION_CAP_WARNING_LEAD_MS (2 min); set small in tests.
   */
  sessionCapWarningLeadMs?: number;
  /**
   * Grace after the tab is hidden before we auto-pause (tab-switch teardown).
   * Defaults to HIDDEN_GRACE_MS (15s); set small in tests.
   */
  hiddenGraceMs?: number;
  /**
   * Frontend hint (route/tab the user opened voice from) so n8n can personalise
   * the greeting. Captured at connect time; defaults to the current path+query.
   * Richer on-screen detail is deferred to the MCP context surface (13c).
   */
  currentPage?: string;
  /** Prior conversation to recall details from (post-13c cross-session recall). */
  previousConversationId?: string;
  onWidgetData?: (data: { event: string; widget_id?: string; data: Record<string, unknown> }) => void;
  /**
   * Fix B (spec 032) — a spoken-transcript turn the agent published over the
   * data channel (`{ event:'transcript', data:{ role, text } }`). The composing
   * hook routes it into the thread's `turns` through the same content-block path
   * text uses, so voice turns appear live without polling. Distinct from
   * `onWidgetData` (widgets) and the `status_label` override.
   */
  onTranscript?: (data: VoiceTranscriptData) => void;
  /**
   * ANA-575 — the agent intentionally ended the session (goodbye / scope-lock /
   * cap / guardrail). Fired once, on the real disconnect (after the goodbye has
   * played), so the composing hook can dispatch a `cloud_session_ended`
   * lifecycle end that drives the scoping surface to Scoping Locked, or the
   * ended surface for other modes. NOT fired for an `error`-reason end (that
   * keeps the retryable error path).
   */
  onVoiceSessionEnded?: (reason: VoiceEndReason) => void;
  /**
   * 032 Phase D (US2 / ANA-612 / MF7) — the server resolved the supplied
   * conversation id to a DIFFERENT one at `/voice/token` (an owned-but-expired
   * conversation is born on a fresh id). Fired BEFORE `room.connect`, so the
   * composing hook can re-seat its authoritative session id and the whole
   * client (transcript hydrate, reconnect token requests, exposed id) tracks the
   * room the server actually minted — closing the MF7 split-brain. Only fires on
   * an ACTUAL change (server echoed a different id); a verbatim echo is a no-op.
   * Present in the response only when `CONVERSATION_ID_REPLACED_EMIT_ENABLED` is
   * on server-side, so this is dark until the coordinated flag flip.
   */
  onConversationIdResolved?: (resolvedConversationId: string) => void;
  onError?: (error: Error) => void;
}

/**
 * Why the call is paused. `manual` = the user tapped Pause; `idle` = auto-paused
 * after prolonged silence (no mic input); `max_session` = the cost cap released
 * the room; `dropped` = an unexpected transport disconnect (network blip that
 * gave up, server hiccup) while the call was active. Drives the paused status
 * copy the surface shows.
 */
export type VoicePauseReason = 'manual' | 'idle' | 'max_session' | 'dropped';

export interface VoiceConversationReturn {
  voiceState: VoiceState;
  token: string | null;
  livekitUrl: string | null;
  isConnected: boolean;
  isMuted: boolean;
  paused: boolean;
  /** Why the current pause happened (null when not paused). */
  pauseReason: VoicePauseReason | null;
  elapsedPauseMs: number;
  /**
   * Establish the LiveKit voice session.
   *
   * Resolves with `{ ok: true }` only when the LiveKit room successfully
   * joined and is ready to publish/subscribe. Resolves with `{ ok: false }`
   * for any failure (token mint error, ICE failure, room full, etc.) — caller
   * MUST not assume a working session unless `ok === true`.
   *
   * The hook also flips its own state on failure (`voiceState='error'`,
   * `setError(...)`, fires `onError`), so consumers that just want to surface
   * the error path do not need to inspect the return value.
   *
   * Why a return flag instead of post-await state read: `useVoiceConversation`
   * is wrapped by `useCallback` in consumers; a post-await `voiceHook.isConnected`
   * read evaluates against the stale captured render snapshot, not the
   * post-connect state, so it returns `false` on the very first connect even
   * when transport succeeded. The explicit return value is the only safe
   * signal.
   */
  connect: (opts?: { expectGreeting?: boolean; greetingTimeoutIsHardError?: boolean }) => Promise<{ ok: boolean }>;
  disconnect: () => void;
  pause: (reason?: VoicePauseReason) => void;
  resume: () => Promise<void>;
  consumeElapsedPause: () => number;
  toggleMute: () => void;
  /** Clear a terminal voice error and return to idle (e.g. on switch back to text). */
  clearError: () => void;
  error: Error | null;
  /** Current LiveKit room after connect; read on `voiceState` updates (ref is not reactive). */
  room: Room | null;
  /**
   * True when the microphone could not be published on connect (e.g. the user
   * denied the browser permission prompt). The session still connects — the
   * agent can speak — so consumers should surface this as a soft "mic blocked"
   * hint rather than a hard error.
   */
  micBlocked: boolean;
  /**
   * Coarse classification of WHY the mic publish failed (null when the mic is
   * fine). Lets the surface show honest, actionable copy instead of a single
   * "enable mic access" hint regardless of the real cause — a `not-found`
   * (missing / stale device) reads very differently from a `denied` permission.
   */
  micErrorKind: MicErrorKind | null;
  /**
   * True while an idle "still there?" prompt is showing — set after
   * `idleListeningTimeoutMs` of silence while `listening`, cleared the moment the
   * user speaks (or the session leaves `listening`). The surface should render a
   * visible "Still there? I'll pause shortly…" message while true; if the grace
   * window (`idleStillThereGraceMs`) elapses with no activity, the call pauses.
   */
  idlePrompting: boolean;
  /**
   * ANA-409 — true once the call is within `sessionCapWarningLeadMs` of the
   * hard cost-cap (`maxSessionMs`). The surface should offer to continue
   * (`extendSession()`) or wrap up (lock the scope) — if neither happens
   * before the cap, the call auto-pauses exactly as it always has
   * (`pauseReason: 'max_session'`). Cleared by `extendSession()` or once the
   * session ends.
   */
  sessionCapWarning: boolean;
  /**
   * ANA-409 — reset the cost-cap window to a fresh `maxSessionMs`/
   * `sessionCapWarningLeadMs` cycle from now, and clear `sessionCapWarning`.
   * Client-only (no reconnect, no server call) — the LiveKit token TTL already
   * covers a full cap cycle.
   */
  extendSession: () => void;
  /**
   * ANA-542 — true once "Lock the scope" has been tapped on the current
   * `sessionCapWarning` cycle. Guards against a duplicate `Lock the scope.`
   * turn being sent. Lives here (shared singleton hook, not component-local
   * state) because `ChatThread` and `WiredConversationInputBar` are two
   * separate mount points for the same wind-down banner (peek/detailed vs
   * focused shell mode) — component-local guards each reset on remount, so
   * switching shell mode between the first tap and its response landing
   * re-opens the window for a second tap to double-publish. Cleared
   * automatically when `sessionCapWarning` clears (a fresh cap cycle, or
   * `extendSession()`), same as the banner itself.
   */
  lockScopeRequested: boolean;
  /** ANA-542 — mark "Lock the scope" as requested for the current warning cycle; idempotent. */
  requestLockScope: () => void;
  /**
   * Custom mid-turn status label pushed by n8n over the agent data channel
   * (spec 015 US4), shown in place of the default "Thinking" label until the
   * next agent-state change. Null when no override is active.
   */
  statusLabelOverride: string | null;
  /**
   * ANA-575 — the reason the agent ended the session, set once `voiceState`
   * reaches `'ended'` (null otherwise). Lets the surface render reason-aware
   * close copy (e.g. cap → "Time's up", guardrail → hard close).
   */
  endedReason: VoiceEndReason | null;
  /**
   * ANA-728 (B1) — true while the mic is AUTO-locked during `thinking` (Anaka is
   * working). The surface shows an honest "mic paused — tap the mic to interrupt"
   * cue (distinct from a user's own mute) and the mic button acts as the one-tap
   * override. Only ever true when the feature flag is on and a track exists.
   */
  micLocked: boolean;
}

export function useVoiceConversation(options: VoiceConversationOptions): VoiceConversationReturn {
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  // Mirror voiceState in a ref so room-event handlers (registered once at connect)
  // can read the CURRENT state without a stale closure — needed to tell a mid-turn
  // agent drop (error) from a normal end-of-turn departure (clean disconnect).
  const voiceStateRef = useRef<VoiceState>('idle');
  voiceStateRef.current = voiceState;
  const [token, setToken] = useState<string | null>(null);
  const [livekitUrl, setLivekitUrl] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  // Ref mirror so toggleMute can read the current muted state without a stale
  // closure (the callback has no dep on isMuted to stay stable).
  const isMutedRef = useRef(false);
  // Monotonic counter incremented on each toggleMute call. The async error
  // callback captures the counter value at call time and skips the rollback if
  // a newer toggle has already superseded it, preventing stale-closure races on
  // rapid mute/unmute bursts where an older rejected promise settles last.
  const muteSeqRef = useRef(0);
  const [error, setError] = useState<Error | null>(null);
  const [paused, setPaused] = useState(false);
  const [pauseReason, setPauseReason] = useState<VoicePauseReason | null>(null);
  const [elapsedPauseMs, setElapsedPauseMs] = useState(0);
  const [micBlocked, setMicBlocked] = useState(false);
  const [micErrorKind, setMicErrorKind] = useState<MicErrorKind | null>(null);
  // Ref mirrors so the greeting-timeout closure (armed at connect) reads the
  // LATEST mic state — a mic failure classified after the timeout was scheduled
  // must still make the timeout's error message honest (device-in-use, not a
  // generic "couldn't reach Anaka").
  const micBlockedRef = useRef(false);
  const micErrorKindRef = useRef<MicErrorKind | null>(null);
  useEffect(() => {
    micBlockedRef.current = micBlocked;
    micErrorKindRef.current = micErrorKind;
  }, [micBlocked, micErrorKind]);
  // Spec 015 (US4): a custom status label pushed by n8n (via the agent data
  // channel) that replaces the default "Thinking" status until the next
  // agent-state change. Null when no override is active.
  const [statusLabelOverride, setStatusLabelOverride] = useState<string | null>(null);
  // IP-260703 — a monotonic counter bumped on EVERY status_label from the agent
  // (even a repeat of the same label), used to re-arm the thinking-stall watchdog.
  // Keying the watchdog on `statusLabelOverride` alone would miss a heartbeat that
  // repeats the same label (no state change → no re-arm), false-erroring a
  // long-but-alive tool-loop turn. A counter changes on every heartbeat.
  const [thinkingHeartbeat, setThinkingHeartbeat] = useState(0);
  // True while the local user is actively speaking (LiveKit ActiveSpeakers).
  // Real user speech is activity that must suspend the idle auto-pause timer —
  // the agent stays in `lk.agent.state='listening'` for the WHOLE user
  // utterance (it only flips to `thinking` AFTER end-of-turn), so `voiceState`
  // alone can't tell "waiting on the user" from "user mid-sentence". Without
  // this, a spoken answer longer than the idle window trips auto-pause and
  // drops the call mid-sentence.
  const [userSpeaking, setUserSpeaking] = useState(false);
  // True while the idle "still there?" prompt is showing (phase 1 of the idle
  // auto-pause). Cleared the moment the user speaks or the session leaves
  // `listening`. The grace timer (phase 2) lives on `idleCloseTimerRef`.
  const [idlePrompting, setIdlePrompting] = useState(false);
  // ANA-728 (B1 / R7) — true while WE hold the mic auto-locked during `thinking`.
  // Drives the honest "mic paused — tap to interrupt" indicator. Distinct from a
  // user's manual mute (`isMuted`). Default-off feature (VITE_VOICE_MIC_LOCK).
  const [micLocked, setMicLocked] = useState(false);

  const roomRef = useRef<Room | null>(null);
  // Phase-2 idle timer: armed when the "still there?" prompt fires, pauses the
  // call if the grace window elapses with no activity. Held on a ref so the idle
  // effect's cleanup can clear it when the user becomes active.
  const idleCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pausedRef = useRef(false);
  const pauseStartedAtRef = useRef<number>(0);
  // Total accumulated paused wall-time across all pause/resume cycles, until consumed.
  const elapsedPauseRef = useRef(0);

  // True while a `room.connect()` handshake is in flight. Used to avoid calling
  // `room.disconnect()` mid-handshake (which aborts the connect and surfaces a
  // "Client initiated disconnect" error + leaves LiveKit retrying
  // /settings/regions on a half-open socket).
  const connectingRef = useRef(false);
  // FR-001a (spec 032) — the conversation id of the current live / in-flight
  // room. The live-room guard in `performConnect` reads it to no-op a repeat
  // connect for the SAME conversation (effect churn / a resolved-id change)
  // instead of tearing the room down + reconnecting — the CLIENT_INITIATED
  // reconnect storm. A stale value is harmless: the guard only fires while a
  // room is live or a handshake is in flight.
  const activeConversationIdRef = useRef<string | null>(null);
  // Set when a disconnect (explicit, or from unmount cleanup) is requested while
  // a connect is still in flight. The connect path checks this AFTER the
  // handshake settles and tears the room down cleanly then.
  const pendingDisconnectRef = useRef(false);
  // Timer that ends the `preparing` (loading) phase if the agent never speaks.
  const preparingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The voiceState captured when a mid-session reconnect began, so we can
  // restore it (e.g. 'preparing') on RoomEvent.Reconnected instead of forcing
  // 'listening' and skipping the greeting phase.
  const preReconnectStateRef = useRef<VoiceState | null>(null);
  // Last `lk.agent.state` we acted on, so a re-published (unchanged) attribute
  // doesn't clear a mid-turn status_label override or churn renders (spec 015).
  const lastAgentStateRef = useRef<string | null>(null);
  // ANA-727 (A1 / R5) — when the CURRENT thinking episode began (ms). Set on the
  // first `listening→thinking` of a turn, cleared on `listening`/terminal — NOT on
  // a `speaking`/`reconnecting` excursion — so the absolute ceiling bounds the whole
  // turn, not each heartbeat re-arm.
  const thinkingEnteredAtRef = useRef<number | null>(null);
  // ANA-727 (A2 / R3) — when the user's current speech episode began (ms), and its
  // measured duration at the falling edge, so the listening watchdog only arms after
  // a SUBSTANTIAL utterance (filters coughs/blips Flux declines to commit).
  const speechStartedAtRef = useRef<number | null>(null);
  // ANA-727 (A2 / R3) — the pending listening-stall timer + its absolute deadline
  // (kept across a suspend so a short "hello?" interjection can't permanently
  // disarm the watchdog) + the previous `userSpeaking` value for edge detection.
  const listeningStallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listeningStallDeadlineRef = useRef<number | null>(null);
  const prevUserSpeakingRef = useRef(false);
  // ANA-728 (B1 / R7) — true while WE hold the mic locked (auto-muted during
  // thinking). Distinct from a user's manual mute; drives lock/restore + the honest
  // indicator. `micLockOverrideRef` latches a manual unmute-during-lock so we don't
  // re-lock for the remainder of the same turn.
  const micLockGraceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const micLockOverrideRef = useRef(false);
  // True only while the lock we applied is in force — so restore ONLY ever
  // re-enables a mic WE disabled from an enabled state (never force-unmutes a
  // deliberately-muted user — the R7 privacy fix).
  const micLockedByUsRef = useRef(false);

  const onWidgetDataRef = useRef(options.onWidgetData);
  onWidgetDataRef.current = options.onWidgetData;
  const onTranscriptRef = useRef(options.onTranscript);
  onTranscriptRef.current = options.onTranscript;
  const onErrorRef = useRef(options.onError);
  onErrorRef.current = options.onError;
  const onVoiceSessionEndedRef = useRef(options.onVoiceSessionEnded);
  onVoiceSessionEndedRef.current = options.onVoiceSessionEnded;
  const onConversationIdResolvedRef = useRef(options.onConversationIdResolved);
  onConversationIdResolvedRef.current = options.onConversationIdResolved;

  // ANA-575 — armed the moment the agent stamps its `anaka.session_end`
  // attribute (a graceful reason: complete / scope_locked / cap / guardrail).
  // While armed: the disconnect guards paint the terminal `'ended'` state
  // instead of `failVoice`/`'disconnected'`, and the preparing/thinking
  // watchdogs stand down (the agent is leaving on purpose). `error`-reason ends
  // do NOT arm this — they keep the retryable error path. Reset on every fresh
  // connect + on `disconnect()` so a prior session's end can't leak into the
  // next one (latch leak). Refs (not state) so the room-event handlers read the
  // latest value without a stale closure.
  const intentionalEndRef = useRef(false);
  const endedReasonRef = useRef<VoiceEndReason | null>(null);
  const [endedReason, setEndedReason] = useState<VoiceEndReason | null>(null);
  // Guards `onVoiceSessionEnded` firing exactly once across the attribute →
  // ParticipantDisconnected → Disconnected sequence for a single end.
  const sessionEndFiredRef = useRef(false);
  const resetIntentionalEnd = useCallback(() => {
    intentionalEndRef.current = false;
    endedReasonRef.current = null;
    sessionEndFiredRef.current = false;
    setEndedReason(null);
  }, []);

  const isConnected =
    voiceState === 'preparing' ||
    voiceState === 'listening' ||
    voiceState === 'thinking' ||
    voiceState === 'speaking';

  const clearPreparingTimeout = useCallback(() => {
    if (preparingTimeoutRef.current) {
      clearTimeout(preparingTimeoutRef.current);
      preparingTimeoutRef.current = null;
    }
  }, []);

  // IP-260703 (client recovery) — tear the dead room down and surface a retryable
  // error via the existing 'error' voiceState (the UI already renders it with a
  // Retry button). Used when the agent drops mid-turn or a turn stalls in
  // `thinking` — turning an infinite "Thinking" spinner into a clean, actionable
  // failure regardless of WHY the agent went away (redeploy, crash, scale-in).
  // ANA-728 (B1) — drop all mic-lock state so a lock can never leak across a
  // teardown/reconnect (leaving a mic disabled with the UI showing unlocked).
  const resetMicLock = useCallback(() => {
    if (micLockGraceTimerRef.current) {
      clearTimeout(micLockGraceTimerRef.current);
      micLockGraceTimerRef.current = null;
    }
    micLockedByUsRef.current = false;
    micLockOverrideRef.current = false;
    setMicLocked(false);
  }, []);

  const failVoice = useCallback((message: string) => {
    try { roomRef.current?.disconnect(); } catch { /* ignore */ }
    roomRef.current = null;
    clearPreparingTimeout();
    setUserSpeaking(false);
    resetMicLock();
    const err = new Error(message);
    setError(err);
    setVoiceState('error');
    onErrorRef.current?.(err);
  }, [clearPreparingTimeout, resetMicLock]);

  const fetchToken = useCallback(async (reconnect?: { elapsedPauseMs?: number }) => {
    // Route/tab the user opened voice from, so n8n can personalise the greeting.
    // Caller may override via options.currentPage; default to the live location.
    const currentPage =
      options.currentPage ??
      (typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`.slice(0, 256)
        : undefined);
    const response = await fetch('/api/v1/conversation/voice/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        conversation_id: options.conversationId,
        // Home/lobby is project-less — only send project_id when we actually
        // have one, so the (now optional) server schema accepts the home case.
        ...(options.projectId ? { project_id: options.projectId } : {}),
        mode: options.mode,
        ...(options.activityId ? { activity_id: options.activityId } : {}),
        ...(options.participantId ? { participant_id: options.participantId } : {}),
        ...(currentPage ? { current_page: currentPage } : {}),
        // Prior conversation to recall from (post-13c cross-session recall).
        ...(options.previousConversationId
          ? { previous_conversation_id: options.previousConversationId }
          : {}),
        // Resume context: set only when reconnecting after a pause.
        ...(reconnect ? { is_reconnect: true } : {}),
        ...(reconnect?.elapsedPauseMs !== undefined
          ? { elapsed_pause_ms: reconnect.elapsedPauseMs }
          : {}),
      }),
    });

    if (!response.ok) throw new Error(`Token request failed: ${response.status}`);
    // 032 Phase D (US2) — `conversation_id` is present only when the server's
    // born-canonical flag is on; absent otherwise (byte-identical to today).
    return response.json() as Promise<{ token: string; url: string; conversation_id?: string }>;
  }, [
    options.conversationId, options.projectId, options.mode, options.activityId,
    options.participantId, options.currentPage, options.previousConversationId,
  ]);

  // Internal: do the actual fetch+connect work; throws on failure so callers
  // (connect / resume) can decide how to surface the error and whether to flip
  // additional state (e.g. resume must not clear `paused` on failure).
  const performConnect = useCallback(async (
    expectGreeting: boolean,
    reconnect?: { elapsedPauseMs?: number },
    // Bug B (ANA-614) — whether a greeting that never arrives is a HARD error
    // (cold start: a missing greeting means a broken pipeline, spec 025 FR-006)
    // or a SOFT degrade to 'listening' (a resume: the spoken recap is a full LLM
    // turn that can be slow/absent, but the session is otherwise fine — the user
    // can talk, and a late recap still animates via the agent-state handler).
    // Defaults true so a bare connect() keeps the cold-start error behaviour.
    greetingTimeoutIsHardError = true,
  ) => {
    console.info('[useVoiceConversation] connect:start', {
      conversationId: options.conversationId,
      projectId: options.projectId,
      mode: options.mode,
      expectGreeting,
    });
    // FR-001a — LIVE-ROOM GUARD. A repeat connect for the conversation we're
    // already connected to (or handshaking) is a spurious re-key (effect churn /
    // a resolved-id change upstream). Do NOT disconnect + reconnect — that IS the
    // CLIENT_INITIATED reconnect storm (audio silence). No-op and keep the live
    // session. `reconnect` (pause→resume) is exempt: it runs from a torn-down
    // room and legitimately re-establishes the transport.
    if (
      !reconnect &&
      options.conversationId &&
      activeConversationIdRef.current === options.conversationId &&
      (roomRef.current || connectingRef.current)
    ) {
      console.info('[useVoiceConversation] connect:already-live-same-id — skipping re-key (FR-001a)', {
        conversationId: options.conversationId,
      });
      return;
    }
    activeConversationIdRef.current = options.conversationId ?? null;
    // Phased connect statuses so the (potentially long, cold-start) connect
    // window reads as progress rather than one frozen state:
    //   fresh:    authorizing → connecting → preparing (Getting ready…) → listening
    //   resume:   reconnecting → … → listening
    setVoiceState(reconnect ? 'reconnecting' : 'authorizing');
    setError(null);
    setMicBlocked(false);
    setMicErrorKind(null);
    setStatusLabelOverride(null);
    setUserSpeaking(false);
    lastAgentStateRef.current = null;
    // ANA-575 — a fresh connect must NOT inherit a prior session's intentional-end
    // latch, or this session's first genuine mid-turn drop would be swallowed as a
    // graceful 'ended' instead of surfacing as a retryable error (latch leak).
    resetIntentionalEnd();
    resetMicLock();
    connectingRef.current = true;
    pendingDisconnectRef.current = false;
    if (roomRef.current) {
      roomRef.current.disconnect();
      roomRef.current = null;
    }

    let tokenResponse: { token: string; url: string; conversation_id?: string };
    try {
      tokenResponse = await fetchToken(reconnect);
      // Safely parse scheme and host for logging without crashing on malformed URLs.
      const scheme = tokenResponse.url.split('://')[0];
      let host = '(unparseable)';
      try {
        host = new URL(tokenResponse.url.replace(/^ws/, 'http')).host;
      } catch { /* leave as unparseable */ }
      console.info('[useVoiceConversation] connect:token-ok', {
        urlScheme: scheme,
        urlHost: host,
      });
    } catch (err) {
      console.error('[useVoiceConversation] connect:token-failed', { error: err });
      throw err;
    }

    // 032 Phase D (US2 / ANA-612 / MF7) — born-canonical adopter. The server may
    // have resolved the supplied id to a DIFFERENT one (an owned-but-expired
    // conversation is minted fresh and the room is keyed on it). Adopt it BEFORE
    // `room.connect` so the client's authoritative session id tracks the room the
    // server actually minted — otherwise the room lives on `resolvedId` while the
    // client's turn/hydrate/reconnect path stays on the stale id (the MF7
    // split-brain). Only act on an ACTUAL change; a verbatim echo (or an absent
    // field when the flag is off) is a no-op. `activeConversationIdRef` is re-seated
    // too so the FR-001a live-room guard tracks the resolved id.
    const resolvedConversationId = tokenResponse.conversation_id;
    if (
      resolvedConversationId &&
      resolvedConversationId !== options.conversationId
    ) {
      console.info('[useVoiceConversation] connect:id-adopted (born-canonical)', {
        supplied: options.conversationId,
        resolved: resolvedConversationId,
      });
      activeConversationIdRef.current = resolvedConversationId;
      onConversationIdResolvedRef.current?.(resolvedConversationId);
    }

    const { token: jwt, url } = tokenResponse;
    setToken(jwt);
    setLivekitUrl(url);

    // Token acquired — now establishing the room/transport. A reconnect keeps
    // its 'reconnecting' label through the whole handshake.
    if (!reconnect) setVoiceState('connecting');

    const { Room, RoomEvent, DisconnectReason } = await import('livekit-client');
    const room = new Room();
    roomRef.current = room;

    // ANA-575 — terminal close for an INTENTIONAL end. Called from the disconnect
    // guards (below) once the agent has left / the room closed AFTER stamping its
    // `anaka.session_end` attribute. Tears our side down cleanly, paints the
    // terminal `'ended'` state (which later `disconnected`/`error` events cannot
    // overwrite — roomRef is nulled first, so those handlers no-op on the
    // `roomRef.current === room` guard), and fires `onVoiceSessionEnded` exactly
    // once so the composing hook can dispatch the `cloud_session_ended` lifecycle
    // end. Deliberately runs on the disconnect, NOT on the attribute, so Anaka's
    // spoken goodbye plays out under `'speaking'` before the surface says "ended".
    const finishIntentionalEnd = () => {
      if (roomRef.current !== room) return;
      const reason = endedReasonRef.current ?? 'complete';
      try { room.disconnect(); } catch { /* ignore */ }
      roomRef.current = null;
      clearPreparingTimeout();
      setUserSpeaking(false);
      setEndedReason(reason);
      setVoiceState('ended');
      if (!sessionEndFiredRef.current) {
        sessionEndFiredRef.current = true;
        onVoiceSessionEndedRef.current?.(reason);
      }
    };

    // ANA-575 (PR #1054 P1) — the SYNCHRONOUS half of the intentional-end read.
    // The attribute-change handler above arms the latch on
    // `ParticipantAttributesChanged`, but LiveKit does NOT fire that event for an
    // attribute that was ALREADY present when we first subscribed to the
    // participant (e.g. a reconnect that re-subscribes to an agent that already
    // stamped its end, or any ordering where the disconnect is observed without a
    // preceding change callback). So the disconnect guards also read the RETAINED
    // `anaka.session_end` straight off the participant — the synchronous read the
    // attribute contract promises — and arm from it before falling through to
    // `failVoice`. Returns true when an intentional (non-`error`) end is armed.
    const armIntentionalEndFromAttrs = (
      attrs: Record<string, string> | undefined,
    ): boolean => {
      if (intentionalEndRef.current) return true;
      const reason = parseVoiceEndReason(attrs?.[ANAKA_SESSION_END_ATTR]);
      if (reason && reason !== 'error') {
        intentionalEndRef.current = true;
        endedReasonRef.current = reason;
        return true;
      }
      return false;
    };
    // Track the local user's speech activity so the idle auto-pause timer can
    // treat talking as activity (not silence). LiveKit fires this with the
    // current active-speaker list; the local participant is flagged `isLocal`.
    room.on(
      RoomEvent.ActiveSpeakersChanged,
      (speakers: Array<{ isLocal?: boolean }>) => {
        if (roomRef.current !== room) return;
        const localSpeaking = Array.isArray(speakers) && speakers.some((s) => s?.isLocal);
        setUserSpeaking((prev) => (prev === localSpeaking ? prev : localSpeaking));
      },
    );

    // ANA-728 (B1 / R7) — override detection. If the user UNMUTES the mic while WE
    // hold the auto-lock (the "tap to interrupt" gesture — the bar button toggles
    // the LiveKit track directly, not the hook), release the lock and latch an
    // override for the rest of the turn so a B2 filler's thinking re-entry can't
    // re-lock them. Our OWN restore also fires TrackUnmuted, but it clears
    // `micLockedByUsRef` FIRST, so this only ever treats a genuine user unmute as an
    // override. TrackMuted is `2`/`TrackUnmuted` `... ` — use the enum.
    room.on(RoomEvent.TrackUnmuted, (_pub: unknown, participant?: { isLocal?: boolean }) => {
      if (roomRef.current !== room) return;
      if (participant?.isLocal && micLockedByUsRef.current) {
        micLockedByUsRef.current = false;
        micLockOverrideRef.current = true;
        setMicLocked(false);
      }
    });

    room.on(RoomEvent.DataReceived, (payload: Uint8Array, participant) => {
      if (isAgentParticipant(participant?.identity)) {
        try {
          const text = new TextDecoder().decode(payload);
          if (text.length > 1_000_000) return;
          const parsed = JSON.parse(text);
          if (parsed && typeof parsed.event === 'string' && parsed.data != null && typeof parsed.data === 'object') {
            // Fix B (spec 032): a `transcript` message carries a spoken turn's
            // text (the user's STT or Anaka's ttsText). Route it to onTranscript
            // (→ the thread's `turns`, the same path text uses) instead of the
            // widget path. Always CONSUME a transcript-typed event — even a
            // malformed one — so a bad payload can never leak through as a broken
            // widget.
            if (parsed.event === VOICE_TRANSCRIPT_EVENT) {
              const transcript = parseVoiceTranscriptMessage(parsed);
              if (transcript) onTranscriptRef.current?.(transcript);
              return;
            }
            // Spec 015 (US4): a status_label message overrides the default
            // "Thinking" status; it is not a widget, so handle it here rather
            // than forwarding to onWidgetData.
            if (parsed.event === 'status_label') {
              const label = (parsed.data as { label?: unknown }).label;
              if (typeof label === 'string' && label.length > 0) {
                setStatusLabelOverride(label);
                // Heartbeat: re-arm the thinking-stall watchdog even if the label
                // is unchanged (a repeated "Looking things up" is still liveness).
                setThinkingHeartbeat((n) => n + 1);
              }
              return;
            }
            onWidgetDataRef.current?.(parsed);
          }
        } catch { /* ignore malformed */ }
      }
    });

    // Diagnostic: surface LiveKit connection-state transitions so an unexpected
    // mid-session drop is visible in the console.
    room.on(RoomEvent.ConnectionStateChanged, (state: unknown) => {
      console.info('[useVoiceConversation] room:connection-state', { state });
    });

    // Drive the status line off the full `lk.agent.state` cycle the agent
    // publishes (`listening` → `thinking` → `speaking`) so the surface reflects
    // exactly what Anaka is doing (spec 015 US2). Reaching any concrete agent
    // state also leaves the 'preparing' (greeting-loading) phase.
    room.on(
      RoomEvent.ParticipantAttributesChanged,
      (changed: Record<string, string>, participant: unknown) => {
        if (roomRef.current !== room) return;
        const identity = (participant as { identity?: string } | undefined)?.identity;
        if (!isAgentParticipant(identity)) return;
        const attrs = (participant as { attributes?: Record<string, string> } | undefined)?.attributes;
        // ANA-575 — the agent stamped its intentional-end reason. ARM the latch
        // now (before the ~2.5–18s-later disconnect) so the disconnect guards
        // paint the terminal `'ended'` state and the watchdogs stand down. We do
        // NOT change the visual here — the goodbye is still playing, so we let it
        // finish under `'speaking'`; `finishIntentionalEnd` runs on the actual
        // disconnect. An `error` reason is NOT a graceful end: leave the latch
        // off so the existing retryable-error path handles it.
        const endReason = parseVoiceEndReason(
          changed?.[ANAKA_SESSION_END_ATTR] ?? attrs?.[ANAKA_SESSION_END_ATTR],
        );
        if (endReason && endReason !== 'error') {
          intentionalEndRef.current = true;
          endedReasonRef.current = endReason;
          // A goodbye turn is done processing — stand the thinking/preparing
          // watchdogs down and drop any stale status label.
          clearPreparingTimeout();
          setStatusLabelOverride(null);
        }
        const agentState = changed?.[AGENT_STATE_ATTR] ?? attrs?.[AGENT_STATE_ATTR];
        if (agentState !== 'listening' && agentState !== 'thinking' && agentState !== 'speaking') return;
        // Only react to an actual transition (LiveKit may re-publish the same
        // attribute) so a status_label override isn't cleared mid-thinking.
        if (agentState === lastAgentStateRef.current) return;
        lastAgentStateRef.current = agentState;
        // A fresh agent-state transition clears any mid-turn status_label
        // override so a stale label can't outlive its turn (US4 / FR-LABEL-006).
        setStatusLabelOverride(null);
        setVoiceState((s) => {
          if (s === 'preparing') {
            // Greeting bootstrap: hold the loading state until the greeting
            // audio actually starts; the first 'speaking' means the greeting is
            // now playing → reflect that as 'speaking' ("Responding"), NOT
            // 'listening' (which mislabels Anaka's own greeting as the user's
            // turn). The agent publishes 'listening' once the greeting finishes,
            // and the active-conversation branch below flips us to 'listening'
            // then — the correct moment to await the user. Intermediate
            // thinking/listening states don't leave preparing.
            if (agentState === 'speaking') {
              clearPreparingTimeout();
              return 'speaking';
            }
            return 'preparing';
          }
          // Active conversation: reflect the full lk.agent.state cycle so the
          // status line shows exactly what Anaka is doing (spec 015 US2).
          if (s === 'listening' || s === 'thinking' || s === 'speaking') return agentState;
          // Transport states (connecting / reconnecting / error) are untouched.
          return s;
        });
      },
    );

    // An UNEXPECTED disconnect — server kick (DUPLICATE_IDENTITY), room
    // deleted/closed, agent dispatch failure, or network drop. Without this the
    // hook ignored `Disconnected` entirely and the UI stayed pinned on a dead
    // 'listening' session. Our own teardown paths (disconnect / pause /
    // deferred-disconnect) null `roomRef` BEFORE calling `room.disconnect()`,
    // so the `roomRef.current === room` guard skips intentional disconnects and
    // we only react to drops we did not initiate.
    // LiveKit is transparently re-establishing a dropped connection mid-session
    // (network blip). Surface it as 'Reconnecting…' so the user knows we're
    // recovering rather than frozen; flip back to 'listening' once restored.
    room.on(RoomEvent.Reconnecting, () => {
      if (roomRef.current !== room) return;
      console.warn('[useVoiceConversation] room:reconnecting');
      // Remember the phase we interrupted so Reconnected can restore it (a blip
      // during 'preparing' must not jump straight to 'listening' pre-greeting).
      setVoiceState((s) => {
        if (s !== 'reconnecting') preReconnectStateRef.current = s;
        return 'reconnecting';
      });
    });
    room.on(RoomEvent.Reconnected, () => {
      if (roomRef.current !== room) return;
      console.info('[useVoiceConversation] room:reconnected');
      setVoiceState((s) => (s === 'reconnecting' ? (preReconnectStateRef.current ?? 'listening') : s));
      preReconnectStateRef.current = null;
    });

    room.on(RoomEvent.Disconnected, (reason?: unknown) => {
      const reasonName =
        typeof reason === 'number' && DisconnectReason
          ? (DisconnectReason as Record<number, string>)[reason]
          : undefined;
      console.warn('[useVoiceConversation] room:disconnected', { reason, reasonName });
      if (roomRef.current === room) {
        // ANA-575 — an INTENTIONAL end (the agent stamped its `anaka.session_end`
        // attribute before disconnecting). This wins over every branch below: it
        // is NOT a dropped turn, so suppress `failVoice` even from
        // `thinking`/`preparing`, and paint the terminal `'ended'` state instead
        // of `'disconnected'`. ANA-592: unlike `ParticipantDisconnected` below (whose
        // event carries the leaving participant's attributes directly), a whole-room
        // `Disconnected` has no synchronous fallback available — LiveKit's
        // `Room.handleDisconnect` clears `remoteParticipants` before this event fires,
        // so reading attributes off the room here always finds nothing (verified
        // against `livekit-client`'s `Room.ts`). This relies solely on the
        // event-driven `intentionalEndRef` latch armed by `ParticipantAttributesChanged`.
        if (intentionalEndRef.current) {
          console.info('[useVoiceConversation] room:disconnected — intentional end', { reason: endedReasonRef.current });
          finishIntentionalEnd();
          return;
        }
        // IP-260703 / ANA-479: a whole-room drop while we're waiting on a
        // response (`thinking`) OR still waiting on the greeting to start
        // (`preparing`) hangs the spinner — surface a retryable error rather
        // than a silent 'disconnected'. Both phases have no legitimate
        // "goodbye" — the agent never intentionally ends a call before it has
        // said anything. Other phases fall through to the clean teardown.
        if (voiceStateRef.current === 'thinking' || voiceStateRef.current === 'preparing') {
          console.warn('[useVoiceConversation] room dropped mid-thinking/preparing — surfacing retryable error');
          failVoice('Lost the connection to Anaka. Tap to try again.');
          return;
        }
        // ANA-480 — the guard above only catches a DIRECT thinking/preparing
        // drop. A network blip first flips voiceState to 'reconnecting'
        // (RoomEvent.Reconnecting, stashing the interrupted phase in
        // preReconnectStateRef); if that reconnect attempt then fully gives up,
        // Disconnected fires with voiceStateRef.current === 'reconnecting',
        // which the guard above doesn't match. Without this check it fell
        // through to the pause/resume path below — fine for a drop while
        // idle-listening, but wrong for a dropped turn (the response itself
        // was lost, same as the direct case) — so consult the stashed phase
        // and treat it identically to a direct thinking/preparing drop.
        if (
          voiceStateRef.current === 'reconnecting' &&
          (preReconnectStateRef.current === 'thinking' || preReconnectStateRef.current === 'preparing')
        ) {
          console.warn('[useVoiceConversation] reconnect gave up mid-thinking/preparing — surfacing retryable error');
          failVoice('Lost the connection to Anaka. Tap to try again.');
          return;
        }
        roomRef.current = null;
        clearPreparingTimeout();
        setUserSpeaking(false);
        // An unexpected drop from any other active state (most commonly
        // 'listening') must not look like "never started" — the prior bare
        // `setVoiceState('disconnected')` here rendered identically to idle
        // (mapVoiceVisualState: same "Ready when you are" label/accent), so
        // `resume()` silently no-op'd on `pausedRef.current === false` and the
        // user's only way back in was a fresh `connect()`, minting a new
        // conversation id each time. Route it through the same pause
        // bookkeeping `pause()` uses so the existing "Restart" control calls
        // `resume()` — reusing the frozen conversation id and sending
        // `is_reconnect` — instead of starting over. (Real-world incident:
        // one Cadence-org production scoping call fragmented into 4 separate
        // conversations across repeated network drops, 2026-07-05.)
        if (!pausedRef.current) {
          pausedRef.current = true;
          pauseStartedAtRef.current = Date.now();
          setPaused(true);
          setPauseReason('dropped');
        }
        setVoiceState('disconnected');
      }
    });

    // The AGENT left the room. It self-disconnects ~500ms after an end_session
    // (goodbye / cap / guardrail / error — see agent/src/index.ts), but LiveKit
    // keeps US in the room, so without tearing down here the local mic track
    // stays published and the browser tab keeps showing the recording indicator
    // long after the conversation finished. Disconnect our side too —
    // `room.disconnect()` stops local tracks (stopTracks defaults true) — to
    // release the mic. Distinct from RoomEvent.Disconnected (a whole-room drop)
    // and from a network Reconnecting blip (the transport, not the agent,
    // flaps). Null roomRef first so the disconnect()-driven Disconnected handler
    // above no-ops on the `roomRef.current === room` guard.
    room.on(RoomEvent.ParticipantDisconnected, (participant: unknown) => {
      if (roomRef.current !== room) return;
      const identity = (participant as { identity?: string } | undefined)?.identity;
      if (!isAgentParticipant(identity)) return;
      // ANA-575 — the agent stamped its `anaka.session_end` attribute before
      // leaving: this is the intentional close (goodbye played, now it's gone).
      // Paint the terminal `'ended'` state and fire onVoiceSessionEnded — never
      // the dropped-turn error, even from `thinking`/`preparing`. Read the
      // participant's retained attribute synchronously as a fallback (PR #1054 P1)
      // for the reverse-order case where the change-event arm never fired.
      const leftAttrs = (participant as { attributes?: Record<string, string> } | undefined)?.attributes;
      if (intentionalEndRef.current || armIntentionalEndFromAttrs(leftAttrs)) {
        console.info('[useVoiceConversation] agent left — intentional end', { reason: endedReasonRef.current });
        finishIntentionalEnd();
        return;
      }
      // IP-260703 / ANA-479: the agent leaving WHILE we're waiting on its
      // response (`thinking`) is a DROPPED turn, and leaving WHILE we're still
      // waiting for it to even start speaking (`preparing`) is a dropped
      // connect — both are a redeploy/crash/scale-in tearing down the
      // in-flight session, not the normal ~500ms post-goodbye self-disconnect
      // (there is no legitimate goodbye before the agent has said anything).
      // Surface a retryable error instead of a silent 'disconnected' that would
      // otherwise look identical to a clean idle state.
      if (voiceStateRef.current === 'thinking' || voiceStateRef.current === 'preparing') {
        console.warn('[useVoiceConversation] agent left mid-thinking/preparing — dropped turn; surfacing retryable error');
        failVoice('Lost the connection to Anaka. Tap to try again.');
        return;
      }
      console.info('[useVoiceConversation] agent left the room — tearing down to release the mic');
      roomRef.current = null;
      clearPreparingTimeout();
      setUserSpeaking(false);
      try { room.disconnect(); } catch { /* ignore */ }
      setVoiceState('disconnected');
    });

    try {
      await room.connect(url, jwt);
      console.info('[useVoiceConversation] connect:room-ok', { url });
    } catch (err) {
      console.error('[useVoiceConversation] connect:room-failed', { url, error: err });
      throw err;
    } finally {
      connectingRef.current = false;
    }

    // A disconnect was requested (explicitly or via unmount) while the handshake
    // was in flight. Now that it has settled, tear the room down cleanly rather
    // than having aborted it mid-flight. Bail before flipping to 'listening'.
    if (pendingDisconnectRef.current) {
      console.info('[useVoiceConversation] connect:deferred-disconnect');
      pendingDisconnectRef.current = false;
      try {
        room.disconnect();
      } catch { /* ignore */ }
      roomRef.current = null;
      clearPreparingTimeout();
      setVoiceState('disconnected');
      return;
    }

    if (expectGreeting) {
      // Fresh voice-first start: Anaka greets. Connected at the transport level,
      // but she hasn't spoken yet — show a loading ('preparing') state until her
      // greeting audio actually starts (see the ParticipantAttributesChanged
      // handler above), with a timed fallback so a silent/failed turn can't pin
      // the spinner forever.
      setVoiceState('preparing');
      clearPreparingTimeout();
      const greetingTimeoutMs = options.greetingTimeoutMs ?? PREPARING_FALLBACK_MS;
      preparingTimeoutRef.current = setTimeout(() => {
        if (roomRef.current !== room) return;
        // ANA-575 — the agent stamped an intentional end (e.g. an immediate
        // guardrail/cap close on turn 0): don't surface a greeting-timeout error;
        // the disconnect guards will paint the terminal 'ended' state.
        if (intentionalEndRef.current) return;
        // Bug B (ANA-614) — a RESUME whose spoken recap is slow/absent must NOT
        // error: the transport is up and the user can talk. Degrade 'preparing'
        // → 'listening' (a late recap still flips us to 'speaking' via the
        // agent-state handler's active-conversation branch). Only a cold start
        // keeps the hard error below, where a missing greeting is a real fault.
        if (!greetingTimeoutIsHardError) {
          setVoiceState((s) => (s === 'preparing' ? 'listening' : s));
          return;
        }
        // Greeting never arrived (e.g. the /turn 401 incident, OR a microphone
        // held by another app/device so nothing publishes) — surface an honest,
        // retryable error instead of a silent 'listening' state (spec 025 US1 /
        // FR-006). A held/un-started mic is a common HIDDEN cause: the transport
        // connects but the agent looks unreachable, masking the real problem. We
        // pick the message from the live mic state so the user gets "another app
        // is using your microphone" rather than a generic "couldn't reach Anaka".
        void (async () => {
          if (roomRef.current !== room) return;
          let micEnabled = false;
          try { micEnabled = room.localParticipant.isMicrophoneEnabled === true; } catch { /* ignore */ }
          const micPermission = await readMicPermission();
          if (roomRef.current !== room) return; // torn down while awaiting permission read
          const message = greetingTimeoutMessage({
            micBlocked: micBlockedRef.current,
            micErrorKind: micErrorKindRef.current,
            micEnabled,
            muted: isMutedRef.current,
            micPermission,
          });
          console.warn('[useVoiceConversation] greeting-timeout: GREETING_TIMEOUT', {
            micBlocked: micBlockedRef.current,
            micErrorKind: micErrorKindRef.current,
            micEnabled,
            micPermission,
          });
          // The session is up but the agent is mute (or the mic never came up),
          // so tear the dead room down; the user retries or switches to text via
          // the VoiceIdleZone error state.
          try { room.disconnect(); } catch { /* ignore */ }
          roomRef.current = null;
          const err = new Error(message);
          setError(err);
          setVoiceState('error');
          onErrorRef.current?.(err);
        })();
      }, greetingTimeoutMs);
    } else {
      // Resuming / switching from text: Anaka does NOT greet, so there's no
      // greeting audio to wait for — go straight to the active 'listening'
      // state rather than sitting in a loading state until the fallback fires.
      setVoiceState('listening');
    }

    // Open-mic: publish the user's microphone so the agent hears them
    // immediately (phone-call style). Fire-and-forget so a slow permission
    // dialog doesn't gate the 'listening' state or the connect() resolution.
    // A denied/failed mic must NOT drop the session — the agent can still
    // speak — so we only flag `micBlocked`/`micErrorKind` for the UI to surface
    // a hint. The try/catch also keeps a synchronous throw from
    // `setMicrophoneEnabled` (older API / odd state) from bubbling out and
    // failing the connect.
    //
    // Stale-device recovery: `setMicrophoneEnabled(true)` can reject with
    // `NotFoundError: Requested device not found` even when the browser HAS mic
    // permission and a working mic — livekit-client re-requests a cached
    // `deviceId` (from a prior connect / device that has since gone). A plain
    // `getUserMedia({audio:true})` succeeds in that state, so on any non-denied
    // failure we retry ONCE forcing the system default input device, which
    // bypasses the stale exact-device constraint. (Denied is a hard permission
    // stop — a different device can't help — so we don't burn a second prompt.)
    void (async () => {
      try {
        await room.localParticipant.setMicrophoneEnabled(true);
        // Re-apply mute state: toggleMute may have been called while disconnected
        // (e.g. the user muted before pause/reconnect). Without this the mic
        // publishes open even though isMutedRef=true and the UI shows muted.
        if (isMutedRef.current) {
          void room.localParticipant.setMicrophoneEnabled(false).catch(() => {});
        }
        setMicBlocked(false);
        setMicErrorKind(null);
      } catch (micErr) {
        const kind = classifyMicError(micErr);
        console.warn('[useVoiceConversation] connect:mic-enable-failed', {
          name: (micErr as { name?: string } | null)?.name,
          kind,
        });
        if (roomRef.current !== room) return; // session torn down mid-publish
        if (kind === 'denied') {
          setMicBlocked(true);
          setMicErrorKind('denied');
          return;
        }
        try {
          await room.localParticipant.setMicrophoneEnabled(true, { deviceId: 'default' });
          if (roomRef.current !== room) return;
          if (isMutedRef.current) {
            void room.localParticipant.setMicrophoneEnabled(false).catch(() => {});
          }
          console.info('[useVoiceConversation] connect:mic-enable-recovered', {
            via: 'default-device',
          });
          setMicBlocked(false);
          setMicErrorKind(null);
        } catch (retryErr) {
          const retryKind = classifyMicError(retryErr);
          console.warn('[useVoiceConversation] connect:mic-enable-retry-failed', {
            name: (retryErr as { name?: string } | null)?.name,
            kind: retryKind,
          });
          if (roomRef.current !== room) return;
          setMicBlocked(true);
          setMicErrorKind(retryKind);
          // `not-found` surviving the default-device retry means no input device
          // exists at all — not a stale deviceId (that recovers above) or a device
          // held by another app (which can still hear the agent speak). A voice
          // call with zero possible input can never actually converse, so hard-fail
          // fast here rather than let `preparing`/`listening` continue looking
          // live for up to PREPARING_FALLBACK_MS while nothing the user says is
          // ever captured.
          if (retryKind === 'not-found') {
            console.warn('[useVoiceConversation] connect:no-microphone-hard-fail');
            try { room.disconnect(); } catch { /* ignore */ }
            roomRef.current = null;
            clearPreparingTimeout();
            const err = new Error(micErrorHint('not-found'));
            setError(err);
            setVoiceState('error');
            onErrorRef.current?.(err);
          }
        }
      }
    })();
  }, [fetchToken, options.conversationId, options.projectId, options.mode, options.greetingTimeoutMs, clearPreparingTimeout, resetIntentionalEnd]);

  const handleConnectFailure = useCallback((err: unknown) => {
    connectingRef.current = false;
    clearPreparingTimeout();
    // Ensure the room is fully torn down on failure/abort so LiveKit's
    // region-settings retry loop stops on a half-open connection.
    if (roomRef.current) {
      roomRef.current.disconnect();
      roomRef.current = null;
    }
    const wrapped = err instanceof Error ? err : new Error(String(err));
    // A client-initiated disconnect / cancelled connect is the expected outcome
    // when we tear down an in-flight handshake (e.g. user ended the call, or a
    // brief unmount/remount). Treat it as a benign abort, not a hard error.
    const msg = wrapped.message ?? '';
    const benignAbort =
      msg.includes('Client initiated disconnect') ||
      msg.toLowerCase().includes('cancelled') ||
      msg.toLowerCase().includes('canceled');
    if (benignAbort) {
      console.warn('[useVoiceConversation] connect:benign-abort', msg);
      // A3.4 (ANA-644) — a "client initiated disconnect" during connect is ALSO the
      // shape of the CLIENT_INITIATED reconnect storm (repeated aborts). Capture it
      // with the tab-visibility discriminator so a burst correlated with a hidden tab
      // confirms/refutes the tab-teardown hypothesis. `benign:true` lets the dashboard
      // separate the storm (many, fast) from a single legitimate user-end (one).
      captureVoiceEvent('voice_connect_failed', {
        reason: 'client_initiated_abort',
        benign: true,
        visibility: currentVisibilityState(),
        mic_blocked: micBlockedRef.current,
        mic_error_kind: micErrorKindRef.current,
      });
      pendingDisconnectRef.current = false;
      setError(null);
      setVoiceState('disconnected');
      return;
    }
    // A3.4 (ANA-644 / M7) — a real connect failure, invisible to server logs. Carry
    // the discriminating fields (message + visibility + mic state). Fire-and-forget.
    captureVoiceEvent('voice_connect_failed', {
      reason: 'error',
      benign: false,
      message: msg.slice(0, 200),
      visibility: currentVisibilityState(),
      mic_blocked: micBlockedRef.current,
      mic_error_kind: micErrorKindRef.current,
    });
    setError(wrapped);
    setVoiceState('error');
    onErrorRef.current?.(wrapped);
  }, [clearPreparingTimeout]);

  const connect = useCallback(async (
    opts?: { expectGreeting?: boolean; greetingTimeoutIsHardError?: boolean },
  ): Promise<{ ok: boolean }> => {
    try {
      // Default to expecting a greeting (fresh voice-first start) whose absence
      // is a hard error. A resume caller (Bug B / ANA-614) passes
      // `greetingTimeoutIsHardError: false` so a slow/absent recap degrades to
      // 'listening' instead of surfacing the cold-start error.
      await performConnect(
        opts?.expectGreeting ?? true,
        undefined,
        opts?.greetingTimeoutIsHardError ?? true,
      );
      return { ok: true };
    } catch (err) {
      handleConnectFailure(err);
      return { ok: false };
    }
  }, [performConnect, handleConnectFailure]);

  const disconnect = useCallback(() => {
    // If a connect handshake is still in flight, do NOT disconnect the room
    // synchronously — that aborts the handshake ("Client initiated
    // disconnect"). Flag it; the connect path tears down cleanly once the
    // handshake settles. Still clear UI/token state so the surface returns to
    // idle immediately.
    if (connectingRef.current) {
      console.info('[useVoiceConversation] disconnect:deferred (connect in flight)');
      pendingDisconnectRef.current = true;
    } else {
      roomRef.current?.disconnect();
      roomRef.current = null;
    }
    clearPreparingTimeout();
    // ANA-575 — user-initiated End clears any armed intentional-end latch so the
    // next connect starts clean.
    resetIntentionalEnd();
    setToken(null);
    setLivekitUrl(null);
    setVoiceState('disconnected');
    setMicBlocked(false);
    // Reset pause bookkeeping so a subsequent connect() starts from a clean
    // slate. Without this, a pause() → disconnect() → connect() sequence would
    // leave `paused: true` while voiceState advanced to 'listening'.
    if (pausedRef.current) {
      pausedRef.current = false;
      setPaused(false);
    }
    setPauseReason(null);
    pauseStartedAtRef.current = 0;
    if (elapsedPauseRef.current !== 0) {
      elapsedPauseRef.current = 0;
      setElapsedPauseMs(0);
    }
  }, [clearPreparingTimeout, resetIntentionalEnd]);

  const pause = useCallback((reason?: VoicePauseReason) => {
    if (pausedRef.current) return;
    // Sanitize: `pause` is sometimes wired straight to an onClick, so a MouseEvent
    // (or anything not a known reason) must fall back to 'manual', never leak in.
    const resolvedReason: VoicePauseReason =
      reason === 'idle' || reason === 'max_session' ? reason : 'manual';
    pausedRef.current = true;
    pauseStartedAtRef.current = Date.now();
    setPaused(true);
    setPauseReason(resolvedReason);
    clearPreparingTimeout();
    if (roomRef.current) {
      roomRef.current.disconnect();
      roomRef.current = null;
    }
    resetMicLock();
    setVoiceState('disconnected');
  }, [clearPreparingTimeout, resetMicLock]);

  const resume = useCallback(async () => {
    if (!pausedRef.current) return;
    // Total paused wall-time so far (prior cycles + this in-flight pause), sent
    // to n8n so it can adjust the resumed turn.
    const elapsedPauseMs =
      elapsedPauseRef.current + Math.max(0, Date.now() - pauseStartedAtRef.current);
    try {
      // Restart always greets — the server already always sends a spoken
      // recap on a voice reconnect (it never applies the quick-reconnect
      // silent-hydrate suppression to voice; see conversation-gateway.ts's
      // D5 gate, which explicitly excludes inputType==='voice'). Passing
      // expectGreeting=true here just makes the client match that: show
      // 'preparing' and wait for the recap's spoken audio, instead of jumping
      // straight to 'listening' before anything has actually been said.
      // Bug B (ANA-614) — soft timeout: the recap is a full LLM turn that can be
      // slow, so a resume degrades to 'listening' rather than erroring. GATED by
      // the same kill-switch as the connectVoice path (P2 review fix): when the
      // flag is OFF, arm the timeout as a HARD error so a genuinely wedged
      // pause→resume still surfaces the honest FR-006 error — restoring exact
      // prior behaviour on this path too, so `VITE_VOICE_RESUME_GREETING_CYCLE=false`
      // is byte-for-byte across BOTH the connectVoice and pause→resume paths.
      await performConnect(true, { elapsedPauseMs }, !VOICE_RESUME_GREETING_CYCLE_ENABLED);
      // ANA-412: use the pre-await snapshot rather than re-reading Date.now()
      // after performConnect returns, which would incorrectly include the
      // LiveKit connect latency (~1-2 s) in the accumulated pause duration.
      elapsedPauseRef.current = elapsedPauseMs;
      setElapsedPauseMs(elapsedPauseRef.current);
      pausedRef.current = false;
      setPaused(false);
      setPauseReason(null);
    } catch (err) {
      // Resume failure: keep paused=true so the UI can offer a retry, but
      // surface the error like a normal failed connect so error/voiceState
      // are consistent with the rest of the hook's error model.
      handleConnectFailure(err);
    }
  }, [performConnect, handleConnectFailure]);

  const consumeElapsedPause = useCallback((): number => {
    // If a pause is still in progress when the user sends a message, fold the
    // in-flight duration into the accumulator and re-anchor pauseStartedAtRef
    // so the same wall-time can't be double-counted on the eventual resume().
    if (pausedRef.current) {
      const now = Date.now();
      const inProgress = Math.max(0, now - pauseStartedAtRef.current);
      elapsedPauseRef.current += inProgress;
      pauseStartedAtRef.current = now;
    }
    const value = elapsedPauseRef.current;
    elapsedPauseRef.current = 0;
    // Avoid forcing re-renders of every voice-hook consumer on every text
    // message in a text-only session (where the counter is already 0).
    if (value !== 0) setElapsedPauseMs(0);
    return value;
  }, []);

  const toggleMute = useCallback(() => {
    const nextMuted = !isMutedRef.current;
    isMutedRef.current = nextMuted;
    setIsMuted(nextMuted);
    // Actually mute/unmute the published mic track. Without this call the
    // mute button visually toggles but the audio stream keeps transmitting.
    const room = roomRef.current;
    if (room?.localParticipant) {
      // Capture the sequence BEFORE the async call so a concurrent newer toggle
      // can invalidate this callback before it settles.
      const seq = ++muteSeqRef.current;
      room.localParticipant.setMicrophoneEnabled(!nextMuted).catch(() => {
        // A newer toggle already set the desired state — don't overwrite it.
        if (muteSeqRef.current !== seq) return;
        // Revert UI state if the LiveKit API rejects (e.g. no track published).
        isMutedRef.current = !nextMuted;
        setIsMuted(!nextMuted);
      });
    }
  }, []);

  /**
   * Clear a terminal voice error and return to idle. Used when the user switches
   * back to text after a failed connect — `disconnect()` alone leaves `error`
   * set (and voiceState='error') because a failed connect is not "connected",
   * so the stale "Something went wrong" banner would otherwise persist.
   */
  const clearError = useCallback(() => {
    setError(null);
    // ANA-575 — also clear a terminal 'ended' back to idle (e.g. switching to
    // text, or the surface resetting for a fresh conversation), and drop the
    // intentional-end latch/reason so the next connect starts clean.
    setVoiceState((s) => (s === 'error' || s === 'ended' ? 'idle' : s));
    resetIntentionalEnd();
  }, [resetIntentionalEnd]);

  useEffect(() => {
    return () => {
      // ANA-162: always clear the preparing timeout on unmount, regardless of
      // whether a connect is in flight. The previous code skipped
      // clearPreparingTimeout() in the connectingRef path, leaving a window
      // where the timeout could fire on a dead component if performConnect
      // failed before room.connect() was called and the deferred-disconnect path
      // never ran. The deferred-disconnect path (checked after room.connect()
      // settles) also calls clearPreparingTimeout(), so this is defensive
      // hardening rather than a new code path.
      clearPreparingTimeout();
      // ANA-413: clear the phase-2 idle grace timer so pause() can't fire on a
      // dead component after unmount (avoids setState-on-unmounted-component and
      // an unintended room.disconnect() call from the post-unmount pause()).
      if (idleCloseTimerRef.current) {
        clearTimeout(idleCloseTimerRef.current);
        idleCloseTimerRef.current = null;
      }
      // ANA-727/728 — clear the A2 listening-stall timer and the B1 mic-lock grace
      // timer so neither fires on a dead component after unmount.
      if (listeningStallTimerRef.current) {
        clearTimeout(listeningStallTimerRef.current);
        listeningStallTimerRef.current = null;
      }
      if (micLockGraceTimerRef.current) {
        clearTimeout(micLockGraceTimerRef.current);
        micLockGraceTimerRef.current = null;
      }
      // If a connect is in flight at unmount, defer teardown so we don't abort
      // the in-flight LiveKit handshake (which would error as "Client initiated
      // disconnect" and leave the region-settings retry loop running). Flag the
      // pending disconnect; the settled connect path tears the room down.
      if (connectingRef.current) {
        pendingDisconnectRef.current = true;
        return;
      }
      roomRef.current?.disconnect();
    };
  }, [clearPreparingTimeout]);

  // 015 (N4): treat prolonged silence as Pause. While `listening` (awaiting the
  // user), if no activity occurs within idleListeningTimeoutMs, pause() — the
  // same resumable teardown as a manual Pause (paused=true, room disconnected),
  // so we don't hold a billable room on an unattended session but the user can
  // resume.
  //
  // Crucially, the user actively speaking is activity: while `userSpeaking` we
  // suspend the timer entirely, and every speech start/stop re-runs this effect
  // to re-arm a fresh window. We can't rely on `voiceState` alone — the agent
  // stays in `lk.agent.state='listening'` for the whole user utterance (it only
  // flips to `thinking` after end-of-turn), so a spoken answer longer than the
  // idle window would otherwise trip auto-pause mid-sentence.
  useEffect(() => {
    // Any non-listening state or active speech is activity — clear the prompt.
    if (voiceState !== 'listening' || userSpeaking) {
      setIdlePrompting(false);
      return;
    }
    // A blocked/absent mic is NOT idle silence. Auto-pausing here drops the user
    // to a confusing "Restart" while they may be (fruitlessly) talking — and
    // since no local track is published, `userSpeaking` can never trip to hold
    // the timer off. Hold the live state instead so the surface can show an
    // actionable mic hint and let the user retry / switch to text.
    if (micBlocked) return;
    // Two-phase idle (v1.0.4): after `idleListeningTimeoutMs` of silence, surface
    // a "still there?" prompt rather than pausing immediately — common when the
    // user is reading a widget. Only if the `idleStillThereGraceMs` window then
    // elapses with still no activity do we pause (release the billable room).
    const idleMs = options.idleListeningTimeoutMs ?? 60_000;
    const graceMs = options.idleStillThereGraceMs ?? 30_000;
    const promptTimer = setTimeout(() => {
      setIdlePrompting(true);
      // ANA-408 — the visible prompt (ANA-407) is silent on its own; signal the
      // agent to speak a short check-in too. Best-effort: a failed publish still
      // leaves the visible prompt + phase-2 pause working exactly as before.
      publishIdleCheckIn(roomRef.current);
      idleCloseTimerRef.current = setTimeout(() => {
        pause('idle');
      }, graceMs);
    }, idleMs);
    return () => {
      clearTimeout(promptTimer);
      if (idleCloseTimerRef.current) {
        clearTimeout(idleCloseTimerRef.current);
        idleCloseTimerRef.current = null;
      }
    };
  }, [
    voiceState,
    userSpeaking,
    micBlocked,
    options.idleListeningTimeoutMs,
    options.idleStillThereGraceMs,
    pause,
  ]);

  // Cost backstop: hard-cap a single continuous connection (independent of
  // activity). pause() releases the mic + the billable room and is resumable.
  // `isConnected` is stable `true` across the listening/thinking/speaking cycle,
  // but it DOES flip false during a transient `RoomEvent.Reconnecting` blip
  // (voiceState briefly becomes 'reconnecting', which isn't in the isConnected
  // set) even though LiveKit is recovering the SAME room. Without accounting for
  // that, this effect's cleanup clears the timer and the next re-run (once
  // Reconnected restores voiceState) arms a FULL fresh window — letting a network
  // blip (accidental or induced) reset the cap indefinitely (ANA-415). Treat
  // 'reconnecting' as still-live so a transient reconnect can't defeat the cap.
  const sessionLive = isConnected || voiceState === 'reconnecting';
  const [sessionCapWarning, setSessionCapWarning] = useState(false);
  // ANA-409 — bumped by extendSession() to force the cap effect below to
  // re-run and re-arm both timers from now, without depending on any other
  // state change. Not read anywhere except as an effect dependency.
  const [sessionCapResetToken, setSessionCapResetToken] = useState(0);
  const extendSession = useCallback(() => {
    setSessionCapWarning(false);
    setSessionCapResetToken((t) => t + 1);
  }, []);
  // ANA-542 — shared (not per-component) "Lock the scope" tap guard; see the
  // `lockScopeRequested` interface doc above for why this can't live in
  // ChatThread/WiredConversationInputBar local state.
  const [lockScopeRequested, setLockScopeRequested] = useState(false);
  const requestLockScope = useCallback(() => {
    setLockScopeRequested(true);
  }, []);
  useEffect(() => {
    if (!sessionCapWarning) setLockScopeRequested(false);
  }, [sessionCapWarning]);
  useEffect(() => {
    if (!sessionLive) {
      setSessionCapWarning(false);
      return;
    }
    const ms = options.maxSessionMs ?? MAX_SESSION_MS;
    const warningLeadMs = options.sessionCapWarningLeadMs ?? SESSION_CAP_WARNING_LEAD_MS;
    // ANA-409 — warn before the cap so the surface can offer to continue/lock
    // instead of the room being yanked with zero notice. The hard-pause timer
    // below is UNCHANGED: if nothing responds to the warning, auto-pause still
    // fires at exactly `ms`, same as always.
    const warningTimer = setTimeout(() => {
      setSessionCapWarning(true);
    }, Math.max(0, ms - warningLeadMs));
    const timer = setTimeout(() => {
      console.warn('[useVoiceConversation] max-session-reached: pausing to release the room');
      pause('max_session');
    }, ms);
    return () => {
      clearTimeout(warningTimer);
      clearTimeout(timer);
    };
  }, [sessionLive, options.maxSessionMs, options.sessionCapWarningLeadMs, pause, sessionCapResetToken]);

  // IP-260703 (client recovery) — while `thinking` (awaiting Anaka's response),
  // arm a stall watchdog. A real agent-state transition moves voiceState off
  // 'thinking' (re-running this effect → clears the timer); each `status_label`
  // the agent publishes bumps `thinkingHeartbeat` (re-running this effect →
  // re-arms a fresh window) as a liveness heartbeat — a counter, not the label
  // value, so a repeated label still counts. So the timer only fires when
  // the turn is genuinely stalled — a dropped agent (redeploy/crash/scale-in) or
  // a turn that was never dispatched — turning an infinite "Thinking" spinner into
  // a retryable error. Cloud/n8n may have returned fine; this is the browser-side
  // backstop for the leg that went dark.
  useEffect(() => {
    // ANA-727 (A1 / R5) — the ABSOLUTE ceiling is anchored to the thinking EPISODE
    // (a turn), not to each arm/disarm. Clear the anchor only when the turn is done
    // (`listening`) or the surface is terminal/idle; a `speaking` excursion (a B2
    // filler or a recovery line mid-turn) and a `reconnecting` blip do NOT reset it,
    // so the ceiling can't be indefinitely deferred by mid-turn speech (the flaw a
    // per-state ceiling would have) nor by a reconnect (the ANA-415 reset class).
    if (
      voiceState === 'listening' ||
      voiceState === 'idle' ||
      voiceState === 'ended' ||
      voiceState === 'error' ||
      voiceState === 'disconnected'
    ) {
      thinkingEnteredAtRef.current = null;
    }
    if (voiceState !== 'thinking') return;
    if (thinkingEnteredAtRef.current === null) thinkingEnteredAtRef.current = Date.now();
    const enteredAt = thinkingEnteredAtRef.current;

    const stallMs = options.thinkingStallTimeoutMs ?? THINKING_STALL_MS;
    const ceilingMs = options.thinkingCeilingMs ?? THINKING_ABSOLUTE_CEILING_MS;
    // Re-arm to whichever is sooner: the (heartbeat-re-armable) 45s stall, or the
    // REMAINING time on the absolute ceiling. As heartbeats re-run this effect the
    // remaining-ceiling shrinks; once exhausted, armMs hits 0 and fires immediately,
    // so a heartbeat storm can defer the stall but never the ceiling.
    const remainingCeiling = ceilingMs - (Date.now() - enteredAt);
    const armMs = Math.max(0, Math.min(stallMs, remainingCeiling));
    const timer = setTimeout(() => {
      // ANA-575 — a signalled intentional end is a goodbye turn winding down; don't
      // false-error over it.
      if (intentionalEndRef.current) return;
      const ceilingHit = Date.now() - enteredAt >= ceilingMs;
      if (ceilingHit) {
        // R9 — the ceiling fired (heartbeats couldn't defer it). Emit the client
        // signal so the "recovery" half of the success metric is joinable.
        captureVoiceEvent('voice.client.thinking_ceiling', {
          conversation_id: options.conversationId,
          mode: options.mode,
          episode_ms: Date.now() - enteredAt,
        });
      }
      console.warn('[useVoiceConversation] thinking-stall: no response within window — surfacing retryable error', {
        ceilingHit,
      });
      failVoice('Anaka didn’t respond. Tap to try again.');
    }, armMs);
    return () => clearTimeout(timer);
  }, [
    voiceState,
    thinkingHeartbeat,
    options.thinkingStallTimeoutMs,
    options.thinkingCeilingMs,
    options.conversationId,
    options.mode,
    failVoice,
  ]);

  // ANA-727 (A2 / R3) — `listening` liveness watchdog. The incident signature was
  // "user spoke, no EndOfTurn ever committed" → the session sits in `listening`
  // forever (the idle auto-pause measures user SILENCE and is suspended during
  // speech, so it can't catch this). This watches speech EPISODES: after the user
  // speaks a ≥MIN_SPEECH utterance whose falling edge lands with the surface still
  // in `listening` (no `thinking` transition = no committed turn), arm a stall
  // timer; if `listening` persists, surface a retryable error (a dead STT leg).
  //
  // Guards against false teardowns (R3): a mid-utterance MUTE disarms (a
  // deliberately-muted user is never torn down); a `thinking`/`speaking` transition
  // (EOT arrived) fully resets it; a short interjection ("hello?") SUSPENDS rather
  // than cancels, so the incident's own over-talk can't permanently disarm it; a
  // `reconnecting` blip keeps the deadline and re-arms on return. Edge-driven via
  // refs (no render churn). The TEARDOWN is flag-gated (default off → staging first);
  // the telemetry fires regardless so the false-positive rate is measurable.
  useEffect(() => {
    const minSpeech = options.listeningWatchdogMinSpeechMs ?? LISTENING_WATCHDOG_MIN_SPEECH_MS;
    const stallMs = options.listeningStallMs ?? LISTENING_STALL_MS;
    const clearTimer = () => {
      if (listeningStallTimerRef.current) {
        clearTimeout(listeningStallTimerRef.current);
        listeningStallTimerRef.current = null;
      }
    };
    const fire = () => {
      listeningStallTimerRef.current = null;
      listeningStallDeadlineRef.current = null;
      if (intentionalEndRef.current) return;
      // R9 — always emit (measurable even with the teardown off), with join keys.
      const enforce = options.listeningWatchdogEnforced ?? VOICE_LISTENING_WATCHDOG_ENABLED;
      captureVoiceEvent('voice.client.listening_stall', {
        conversation_id: options.conversationId,
        mode: options.mode,
        enforced: enforce,
      });
      if (enforce) {
        console.warn('[useVoiceConversation] listening-stall: spoke but no turn committed — surfacing retryable error');
        failVoice('Anaka didn’t catch that. Tap to try again.');
      }
    };
    const arm = (deadline: number) => {
      listeningStallDeadlineRef.current = deadline;
      clearTimer();
      listeningStallTimerRef.current = setTimeout(fire, Math.max(0, deadline - Date.now()));
    };

    // Disarm conditions.
    if (voiceState !== 'listening' || micBlocked || intentionalEndRef.current) {
      clearTimer();
      // A `thinking`/`speaking` transition means the turn IS progressing (EOT
      // committed) → the watchdog succeeded; full reset. A `reconnecting` blip (or
      // any other non-terminal) keeps the deadline so we can resume on return.
      if (voiceState === 'thinking' || voiceState === 'speaking') {
        listeningStallDeadlineRef.current = null;
        speechStartedAtRef.current = null;
      }
      prevUserSpeakingRef.current = userSpeaking;
      return;
    }

    const prev = prevUserSpeakingRef.current;
    prevUserSpeakingRef.current = userSpeaking;

    if (userSpeaking && !prev) {
      // Rising edge — user (re)started speaking. Suspend any pending window (keep
      // its deadline so a short interjection resumes it, R3(b)).
      speechStartedAtRef.current = Date.now();
      clearTimer();
    } else if (!userSpeaking && prev) {
      // Falling edge — an episode ended.
      const dur = speechStartedAtRef.current !== null ? Date.now() - speechStartedAtRef.current : 0;
      speechStartedAtRef.current = null;
      if (dur >= minSpeech) {
        arm(Date.now() + stallMs); // substantial utterance → fresh full window
      } else if (listeningStallDeadlineRef.current !== null) {
        arm(listeningStallDeadlineRef.current); // short interjection → resume prior window
      }
    } else if (!userSpeaking && !prev) {
      // Steady `listening` with a preserved deadline but no live timer (e.g. just
      // returned from a `reconnecting` excursion) → resume it.
      if (listeningStallDeadlineRef.current !== null && listeningStallTimerRef.current === null) {
        arm(listeningStallDeadlineRef.current);
      }
    }
    // The timer is ref-managed across re-runs; do NOT clear it in cleanup (that
    // would cancel a legitimately-armed window on the next unrelated re-render).
  }, [
    userSpeaking,
    voiceState,
    micBlocked,
    options.listeningStallMs,
    options.listeningWatchdogMinSpeechMs,
    options.conversationId,
    options.mode,
    failVoice,
  ]);

  // ANA-728 (B1 / R7) — auto mic-lock during `thinking`. During the 9–28s n8n
  // round-trip the mic is hot and users talk into the wait → concurrent turns +
  // supersede churn + wedge exposure. Auto-mute the mic during `thinking` and
  // restore it on `listening`/`speaking`, with a visible honest indicator + a
  // one-tap override.
  //
  // Correctness (R7):
  //  - Built on the REAL published-track state, not the hook's `isMutedRef` (the
  //    rendered mic button toggles the LiveKit track directly, NOT the hook), so it
  //    can NEVER force-unmute a deliberately-muted user: we only ever LOCK when the
  //    track is currently enabled, and only ever RESTORE a mic WE locked.
  //  - A grace (MIC_LOCK_GRACE_MS) before locking, so a reflective speaker's
  //    clipped-thought continuation is still HEARD (lands as a supersede); only the
  //    later "hello?" impatience window is closed.
  //  - `micBlocked` (no track) suppresses the whole feature.
  //  - The override latch (`micLockOverrideRef`) — set when the user unmutes during
  //    a lock (see the TrackUnmuted listener at connect) — holds for the rest of the
  //    turn so a B2 filler's `speaking→thinking` re-entry doesn't re-lock them.
  //  - Reset on every turn boundary (`listening`) and teardown (performConnect/
  //    failVoice/pause null the flags).
  useEffect(() => {
    if (!(options.micLockEnabled ?? VOICE_MIC_LOCK_ENABLED)) return;
    const graceMs = options.micLockGraceMs ?? MIC_LOCK_GRACE_MS;
    const room = roomRef.current;
    const lp = room?.localParticipant as
      | { isMicrophoneEnabled?: boolean; setMicrophoneEnabled: (on: boolean) => Promise<unknown> }
      | undefined;

    const clearGrace = () => {
      if (micLockGraceTimerRef.current) {
        clearTimeout(micLockGraceTimerRef.current);
        micLockGraceTimerRef.current = null;
      }
    };

    // A fresh turn: the user is being awaited again → clear the per-turn override
    // latch so the NEXT thinking phase can lock, and drop a stale lock flag.
    if (voiceState === 'listening') {
      micLockOverrideRef.current = false;
    }

    if (!lp || micBlocked) {
      clearGrace();
      return;
    }

    if (voiceState === 'thinking') {
      if (micLockOverrideRef.current || micLockedByUsRef.current) return;
      clearGrace();
      micLockGraceTimerRef.current = setTimeout(() => {
        micLockGraceTimerRef.current = null;
        if (voiceStateRef.current !== 'thinking') return;
        if (micLockOverrideRef.current) return;
        // Only lock a mic that is actually ON — if the user already muted, there's
        // nothing to lock and (critically) nothing we'd later force-unmute.
        if (lp.isMicrophoneEnabled === false) return;
        void Promise.resolve(lp.setMicrophoneEnabled(false))
          .then(() => {
            micLockedByUsRef.current = true;
            setMicLocked(true);
          })
          .catch(() => { /* best-effort; a failed lock just leaves the mic open */ });
      }, graceMs);
      return () => clearGrace();
    }

    // Any non-thinking state → reconcile. If WE locked the mic, restore it.
    clearGrace();
    if (micLockedByUsRef.current) {
      micLockedByUsRef.current = false;
      setMicLocked(false);
      void Promise.resolve(lp.setMicrophoneEnabled(true)).catch(() => {
        // Restore failed (e.g. stale device) — surface mic-blocked so the existing
        // hint takes over rather than leaving a silently-dead mic.
        setMicBlocked(true);
      });
    }
  }, [voiceState, micBlocked, options.micLockEnabled, options.micLockGraceMs]);

  // Tab-switch teardown: when the tab is hidden, pause after a short grace so a
  // billable room + open mic don't keep running while the user is doing
  // something else. A quick tab-flick (visible again before the grace) cancels.
  // We pause on `visibilitychange` rather than trusting the idle auto-pause
  // because background-tab timers are throttled (so the idle timer can fire late
  // on a hidden tab).
  useEffect(() => {
    if (!isConnected) return;
    if (typeof document === 'undefined') return;
    let hideTimer: ReturnType<typeof setTimeout> | null = null;
    const clearHideTimer = () => {
      if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
    };
    const onVisibility = () => {
      if (document.hidden) {
        clearHideTimer();
        hideTimer = setTimeout(() => { pause(); }, options.hiddenGraceMs ?? HIDDEN_GRACE_MS);
      } else {
        clearHideTimer();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      clearHideTimer();
    };
  }, [isConnected, options.hiddenGraceMs, pause]);

  return {
    voiceState,
    token,
    livekitUrl,
    isConnected,
    isMuted,
    paused,
    pauseReason,
    elapsedPauseMs,
    connect,
    disconnect,
    pause,
    resume,
    consumeElapsedPause,
    toggleMute,
    clearError,
    error,
    room: roomRef.current,
    micBlocked,
    micErrorKind,
    idlePrompting,
    sessionCapWarning,
    extendSession,
    lockScopeRequested,
    requestLockScope,
    statusLabelOverride,
    endedReason,
    micLocked,
  };
}
